/**
 * CipherText Generation Utility
 *
 * Collects device fingerprint and location data from the browser,
 * encrypts it into a cipherText that can be validated by the server.
 *
 * The cipherText is used for:
 * - Registration verification
 * - Login verification
 * - Transaction verification
 * - Periodic location checks
 */

import type {
  CipherTextPayload,
  CipherTextOptions,
  CipherTextResult,
  CipherTextReason,
  GeolocationConfigResponse,
} from './types';
import { SDK_VERSION } from '../core/version';
import { getBrowserInfo, generateDeviceId as getOrCreateDeviceId } from '../shared/browser-utils';

const CIPHER_TEXT_EXPIRY_MINUTES = 5;

/**
 * Compute HMAC-SHA256 of a message using the given key.
 * Uses Web Crypto API (browser/modern Node) with node:crypto fallback.
 */
async function computeHMAC(key: string, message: string): Promise<string> {
  // Try Web Crypto API first (works in browsers and Node 18+)
  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.subtle) {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(key);
    const msgData = encoder.encode(message);

    const cryptoKey = await globalThis.crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign'],
    );

    const signature = await globalThis.crypto.subtle.sign('HMAC', cryptoKey, msgData);
    const bytes = new Uint8Array(signature);
    return Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Fallback: Node.js crypto module
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { createHmac } = await import('node:crypto');
  return createHmac('sha256', key).update(message).digest('hex');
}

/**
 * Get WebGL renderer information for fingerprinting
 */
function getWebGLInfo(): { vendor: string; renderer: string } | null {
  if (typeof document === 'undefined') return null;

  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return null;

    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return null;

    return {
      vendor: (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '',
      renderer: (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '',
    };
  } catch {
    return null;
  }
}

/**
 * Get network information if available
 */
function getNetworkInfo(): CipherTextPayload['network'] | undefined {
  if (typeof navigator === 'undefined') return undefined;

  const connection = (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) return undefined;

  return {
    effective_type: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    save_data: connection.saveData,
  };
}

/**
 * Request GPS location from the browser
 */
async function requestGPSLocation(
  timeout: number = 10000,
  highAccuracy: boolean = true
): Promise<CipherTextPayload['location'] | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;

        // Validate coordinates
        if (latitude < -90 || latitude > 90) {
          resolve(null);
          return;
        }
        if (longitude < -180 || longitude > 180) {
          resolve(null);
          return;
        }
        if (accuracy !== null && accuracy !== undefined && accuracy <= 0) {
          resolve(null);
          return;
        }

        resolve({
          latitude,
          longitude,
          accuracy,
          altitude: position.coords.altitude ?? undefined,
          altitude_accuracy: position.coords.altitudeAccuracy ?? undefined,
          heading: position.coords.heading ?? undefined,
          speed: position.coords.speed ?? undefined,
          timestamp: position.timestamp,
        });
      },
      () => {
        // Location denied or error - resolve with null (non-fatal)
        resolve(null);
      },
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeout,
        maximumAge: 0,
      }
    );
  });
}

/**
 * Collect device fingerprint data
 */
function collectDeviceData(includeWebGL: boolean): CipherTextPayload['device'] {
  const browserInfo = getBrowserInfo();
  const webglInfo = includeWebGL ? getWebGLInfo() : null;

  return {
    device_id: getOrCreateDeviceId(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    platform: typeof navigator !== 'undefined' ? navigator.platform : '',
    browser: browserInfo.browser,
    browser_version: browserInfo.browser_version,
    os: browserInfo.os,
    os_version: browserInfo.os_version,
    screen_resolution:
      typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined,
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
    color_depth: typeof screen !== 'undefined' ? screen.colorDepth : undefined,
    hardware_concurrency:
      typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined,
    device_memory: typeof navigator !== 'undefined' ? (navigator as any).deviceMemory : undefined,
    touch_support:
      typeof navigator !== 'undefined' ? navigator.maxTouchPoints > 0 : undefined,
    webgl_vendor: webglInfo?.vendor,
    webgl_renderer: webglInfo?.renderer,
  };
}

/**
 * Encode payload to base64
 */
function encodePayload(payload: CipherTextPayload): string {
  const json = JSON.stringify(payload);

  if (typeof btoa !== 'undefined') {
    // Browser
    return btoa(unescape(encodeURIComponent(json)));
  } else if (typeof Buffer !== 'undefined') {
    // Node.js
    return Buffer.from(json, 'utf-8').toString('base64');
  }

  throw new Error('No base64 encoding method available');
}

/**
 * Generate a cipherText containing encrypted device and location data
 *
 * @param options - Options for cipherText generation
 * @returns CipherText result with the encrypted string
 *
 * @example
 * ```typescript
 * // Basic usage for login
 * const result = await generateCipherText({ reason: 'login' });
 * console.log(result.cipherText);
 *
 * // With GPS location for registration
 * const result = await generateCipherText({
 *   reason: 'registration',
 *   requestLocation: true,
 *   locationTimeout: 15000
 * });
 *
 * if (result.locationCaptured) {
 *   console.log('GPS location included');
 * }
 * ```
 */
export async function generateCipherText(
  options: CipherTextOptions,
  config?: GeolocationConfigResponse
): Promise<CipherTextResult> {
  const warnings: string[] = [];

  // Auto-enable GPS if tenant config requires it for this event type
  let requestLocation = options.requestLocation ?? false;
  let gpsRequiredByConfig = false;
  if (config?.require_gps) {
    const reason = options.reason;
    if (
      (reason === 'login' && config.require_gps.login) ||
      (reason === 'registration' && config.require_gps.registration) ||
      (reason === 'transaction' && config.require_gps.transaction)
    ) {
      if (!requestLocation) {
        gpsRequiredByConfig = true;
      }
      requestLocation = true;
    }
  }

  // Collect device data
  const deviceData = collectDeviceData(options.includeWebGL !== false);

  // Optionally request GPS location
  let locationData: CipherTextPayload['location'] | undefined;
  if (requestLocation) {
    const location = await requestGPSLocation(
      options.locationTimeout || 10000,
      options.highAccuracy !== false
    );

    if (location) {
      locationData = location;
    } else if (gpsRequiredByConfig) {
      throw new Error(
        `GPS location is required for ${options.reason} by tenant configuration, but GPS was not available or permission was denied`
      );
    } else {
      warnings.push('GPS location not available or permission denied');
    }
  }

  // Collect network info
  let networkData: CipherTextPayload['network'] | undefined;
  if (options.includeNetworkInfo !== false) {
    networkData = getNetworkInfo();
  }

  // Build payload
  const now = new Date();
  const expiry = new Date(now.getTime() + CIPHER_TEXT_EXPIRY_MINUTES * 60 * 1000);

  const payload: CipherTextPayload = {
    device: deviceData,
    location: locationData,
    network: networkData,
    metadata: {
      collected_at: now.toISOString(),
      sdk_version: SDK_VERSION,
      collection_reason: options.reason,
      page_url: typeof window !== 'undefined' ? window.location.href : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer || undefined : undefined,
    },
  };

  // Encode payload (no obfuscation - HTTPS provides transport security)
  const encoded = encodePayload(payload);

  // Create final cipherText with version and timestamp prefix
  const timestamp = now.getTime().toString(36); // Base36 timestamp

  let cipherText: string;
  const hmacKey = options.signingKey || options.apiKey;
  if (hmacKey) {
    // v02: HMAC-signed — format: 02.{timestamp}.{base64}.{hmac}
    const message = `02.${timestamp}.${encoded}`;
    const hmac = await computeHMAC(hmacKey, message);
    cipherText = `${message}.${hmac}`;
  } else {
    // v01: unsigned (backwards compat)
    cipherText = `01.${timestamp}.${encoded}`;
  }

  return {
    cipherText,
    locationCaptured: !!locationData,
    warnings: warnings.length > 0 ? warnings : undefined,
    generatedAt: now.toISOString(),
    expiresAt: expiry.toISOString(),
  };
}

/**
 * Decode a cipherText (for debugging/testing - not for production use)
 * In production, decryption happens on the server
 */
export function decodeCipherText(cipherText: string): CipherTextPayload | null {
  try {
    const parts = cipherText.split('.');
    // v01: 3 parts, v02: 4 parts (4th is HMAC — ignored when decoding)
    if (parts.length !== 3 && parts.length !== 4) return null;

    const [version, , encodedB64] = parts;
    if (version !== '01' && version !== '02') return null;

    // Decode matches encode: just base64 decode (no obfuscation)
    const json = decodeURIComponent(escape(atob(encodedB64)));

    return JSON.parse(json) as CipherTextPayload;
  } catch {
    return null;
  }
}

/**
 * Check if a cipherText is expired
 */
export function isCipherTextExpired(cipherText: string): boolean {
  try {
    const parts = cipherText.split('.');
    if (parts.length !== 3 && parts.length !== 4) return true;

    const timestampB36 = parts[1];
    const timestamp = parseInt(timestampB36, 36);
    const now = Date.now();
    const expiryMs = CIPHER_TEXT_EXPIRY_MINUTES * 60 * 1000;

    return now - timestamp > expiryMs;
  } catch {
    return true;
  }
}

// Re-export types
export type { CipherTextPayload, CipherTextOptions, CipherTextResult, CipherTextReason, GeolocationConfigResponse };
